/**
 * Spinning Logo Mode Enhancement
 * Provides additional protection against user interaction for spinning logo videos
 */

class SpinningLogoMode {
  constructor() {
    this.init();
  }

  init() {
    // Find all spinning logo mode videos
    const spinningLogos = document.querySelectorAll(".spinning-logo-mode");

    spinningLogos.forEach((element) => {
      this.enhanceElement(element);
    });

    // Watch for dynamically added spinning logos
    this.observeNewElements();
  }

  enhanceElement(element) {
    // Prevent context menu
    element.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      return false;
    });

    // Prevent drag
    element.addEventListener("dragstart", (e) => {
      e.preventDefault();
      return false;
    });

    // Prevent selection
    element.addEventListener("selectstart", (e) => {
      e.preventDefault();
      return false;
    });

    // Prevent focus
    element.addEventListener("focus", (e) => {
      e.target.blur();
    });

    // Handle video elements specifically
    const videos = element.querySelectorAll("video");
    videos.forEach((video) => {
      this.enhanceVideo(video);
    });

    // Handle iframe elements specifically
    const iframes = element.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      this.enhanceIframe(iframe);
    });
  }

  enhanceVideo(video) {
    // Immediately disable controls before anything else
    video.controls = false;
    video.removeAttribute("controls");

    // Ensure autoplay and loop attributes
    video.setAttribute("autoplay", "true");
    video.setAttribute("loop", "true");
    video.setAttribute("muted", "true");
    video.setAttribute("playsinline", "true");
    video.setAttribute("disablepictureinpicture", "true");
    video.setAttribute("controls", "false");

    // Additional mobile-specific attributes
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("x-webkit-airplay", "deny");
    video.setAttribute("x5-video-player-type", "h5");
    video.setAttribute("x5-video-player-fullscreen", "false");
    video.setAttribute("x5-video-orientation", "portraint");

    // Ensure video is always playing and looping
    video.addEventListener("pause", () => {
      video.play();
    });

    video.addEventListener("ended", () => {
      video.currentTime = 0;
      video.play();
    });

    // Prevent user from stopping the video
    video.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    // Additional mobile-specific event prevention
    video.addEventListener("touchstart", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    video.addEventListener("touchend", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    video.addEventListener("touchmove", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    // Force play if paused
    if (video.paused) {
      video.play().catch(() => {
        // If autoplay fails, try again after a short delay
        setTimeout(() => {
          video.play().catch(() => {});
        }, 100);
      });
    }

    // Additional mobile-specific handling
    const forceMobileBehavior = () => {
      video.controls = false;
      video.removeAttribute("controls");

      // Force styles immediately
      video.style.pointerEvents = "none";
      video.style.userSelect = "none";
      video.style.webkitUserSelect = "none";
      video.style.mozUserSelect = "none";
      video.style.msUserSelect = "none";

      // Hide any potential control overlays
      const controls = video.querySelectorAll("*");
      controls.forEach((control) => {
        if (
          control.classList.contains("controls") ||
          control.classList.contains("media-controls") ||
          control.classList.contains("video-controls")
        ) {
          control.style.display = "none";
        }
      });
    };

    // Apply immediately and on various events
    forceMobileBehavior();
    video.addEventListener("loadstart", forceMobileBehavior);
    video.addEventListener("loadeddata", forceMobileBehavior);
    video.addEventListener("canplay", forceMobileBehavior);
    video.addEventListener("playing", forceMobileBehavior);
  }

  enhanceIframe(iframe) {
    // Prevent iframe interaction
    iframe.addEventListener("load", () => {
      try {
        // Try to disable iframe interaction
        iframe.style.pointerEvents = "none";
        iframe.style.userSelect = "none";
        iframe.style.webkitUserSelect = "none";
        iframe.style.mozUserSelect = "none";
        iframe.style.msUserSelect = "none";
      } catch (e) {
        // Cross-origin restrictions may prevent this
        console.debug("Cannot modify iframe due to cross-origin restrictions");
      }
    });

    // Prevent clicks on iframe
    iframe.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
  }

  observeNewElements() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (
              node.classList &&
              node.classList.contains("spinning-logo-mode")
            ) {
              this.enhanceElement(node);
            }

            // Check for spinning logo mode elements within the added node
            const spinningLogos =
              node.querySelectorAll &&
              node.querySelectorAll(".spinning-logo-mode");
            if (spinningLogos) {
              spinningLogos.forEach((element) => {
                this.enhanceElement(element);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// Immediate initialization for mobile controls prevention
const immediateMobileFix = () => {
  // Apply CSS immediately to prevent controls from showing
  const style = document.createElement("style");
  style.textContent = `
    .spinning-logo-mode video::-webkit-media-controls,
    .spinning-logo-mode video::-webkit-media-controls-panel,
    .spinning-logo-mode video::-webkit-media-controls-play-button,
    .spinning-logo-mode video::-webkit-media-controls-volume-slider,
    .spinning-logo-mode video::-webkit-media-controls-mute-button,
    .spinning-logo-mode video::-webkit-media-controls-timeline,
    .spinning-logo-mode video::-webkit-media-controls-current-time-display,
    .spinning-logo-mode video::-webkit-media-controls-time-remaining-display,
    .spinning-logo-mode video::-webkit-media-controls-fullscreen-button {
      display: none !important;
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
    .spinning-logo-mode video::-moz-media-controls {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  // Find and immediately disable controls on any existing videos
  const existingVideos = document.querySelectorAll(".spinning-logo-mode video");
  existingVideos.forEach((video) => {
    video.controls = false;
    video.removeAttribute("controls");
    video.style.pointerEvents = "none";
  });
};

// Run immediate fix right away
immediateMobileFix();

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new SpinningLogoMode();
  });
} else {
  new SpinningLogoMode();
}
